import * as THREE from 'three';
import { getArcVerticesFromBulge, polylineToSegments } from './get-arc-vertices';
import { renderOrders } from '../../../../viewer/render-orders.ts';

// @ts-ignore
import { dxfHelpers } from '../dxfHelpers';

const compareVertices = (v1, v2) => {
  return v1.x === v2.x && v1.y === v2.y && v1.z === v2.z;
};

const translatePoint3D = (point, towardsPoint, distance) => {
  if (distance === 0) {
    return point;
  }

  const direction = getDirectionVector(point, towardsPoint);
  const magnitude = getMagnitude3D(direction);

  const factor = magnitude / distance;

  direction.x = direction.x / factor;
  direction.y = direction.y / factor;
  direction.z = direction.z / factor;

  const newVector = {
    x: point.x + direction.x,
    y: point.y + direction.y,
    z: point.z + direction.z,
  };
  if (isNaN(newVector.z)) {
    newVector.z = 0;
  }

  return newVector;
};

const getDirectionVector = (v1, v2) => {
  return {
    x: v2.x - v1.x,
    y: v2.y - v1.y,
    z: v2.z - v1.z,
  };
};

const getMagnitude3D = (v) => {
  if (isNaN(v.z)) {
    v.z = 0;
  }
  return Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
};

const getDistance3D = (p1, p2) => {
  const dx = p1.x - p2.x;
  const dy = p1.y - p2.y;

  let dz = p1.z - p2.z;

  if (isNaN(dz)) {
    dz = 0;
  }

  return Math.sqrt(dx * dx + dy * dy + dz * dz);
};

export default class DxfLineParser {
  constructor() {}

  /**
   * Processes the data of the line. Each line will become part of a line segment
   * If there already is a property in vertexCollection that matches this line's
   * layer and line-properties the line will be added to that vertexArray.
   * @param {*} vertexCollection Vertices and properties that have already been processed
   * @param {*} vertices
   * @param {*} properties properties for this line
   * @param {*} layer
   */
  processLineData(entity, insert) {
    let vertices = entity.vertices;
    let is3dPolyline = false;

    let targetArray = [];

    if (vertices.length <= 1 || entity.is3dPolygonMesh || entity.is3dPolygonMeshClosed) {
      return;
    }

    if (entity.is3dPolyline) {
      is3dPolyline = true;
    }

    if (entity.type === 'LWPOLYLINE' && entity.elevation) {
      entity.vertices.map((v) => {
        v.z = entity.elevation;
      });
    }

    if (entity.shape) {
      entity.vertices.push({
        x: entity.vertices[0].x,
        y: entity.vertices[0].y,
        z: entity.vertices[0].z ? entity.vertices[0].z : 0,
      });
    }

    if (insert) {
      vertices = dxfHelpers.transformInsertVertices(insert, vertices);
    }

    for (let i = 0; i < vertices.length; i++) {
      if (!vertices[i].bulge) {
        if (i === vertices.length - 1 && vertices[i - 1].bulge) {
          continue;
        }

        targetArray.push(vertices[i]);

        if (i > 0 && vertices[i - 1].bulge) {
          continue;
        }
      } else {
        if (i > 0 && !vertices[i - 1].bulge) {
          targetArray.push(vertices[i]);
        }

        if (i !== vertices.length - 1) {
          //TODO: Will this work? Just let getPolyArcVertices return the generated vertices...
          let arcVertices = getArcVerticesFromBulge(vertices[i], vertices[i + 1]);
          for (let arc_i = 0; arc_i < arcVertices.length; arc_i++) {
            arcVertices[arc_i].z = vertices[i].z;
            targetArray.push(arcVertices[arc_i]);
          }
        }
      }
    }

    return { vertices: polylineToSegments(targetArray), is3dPolyline: is3dPolyline };

    /* if (index === false) {
      return this.pushAndReturn(vertexCollection, properties, targetArray, layer, is3dPolyline)
    } */
  }

  /**
   * Works like processLineData, but for arc and utilizes DxfArcParser
   */
  processArcData(entity, arcCallback, insert) {
    let targetArray = [];

    let vertices = arcCallback(entity);

    if (insert) {
      vertices = dxfHelpers.transformInsertVertices(insert, vertices);
    }

    for (let i = 0; i < vertices.length; i++) {
      targetArray.push(vertices[i]);
      this.buffer(targetArray, i, vertices);
    }

    return { vertices: targetArray };
    /* if (index === false && vertices.length > 0)
      return this.pushAndReturn(vertexCollection, properties, targetArray, entity.layer); */
  }

  /**
   * Pushes an extra element to the array unless it's the first
   * or last index in the array. This enables the line segments to
   * be rendered correctly since it only draws every other line.
   * @param {*} target target array to modify
   * @param {*} i index for vertices
   * @param {*} vertices array to check
   */
  buffer(target, i, vertices) {
    if (i !== vertices.length - 1 && i !== 0) {
      target.push(vertices[i]);
    }
  }

  /**
   * Generates the correct material depending on the properties
   * @param {*} prop
   */
  generateLineMaterial(prop) {
    if (!prop) {
      prop = {};
    }
    return new THREE.LineBasicMaterial({
      linewidth: prop.lineWidth ? prop.lineWidth : 1,
      color: prop.color ? prop.color : 0,
    });
  }

  /**
   * Takes a complete setup of vertices and the material to
   * generate a line segment from buffer geometry for that line
   */
  generateLines(lineData) {
    const geometry = new THREE.BufferGeometry();
    const material = this.generateLineMaterial(lineData.properties);
    const dottedVertices = lineData.dottedVertices;

    const offset = lineData.dottedVertices[0];
    const vertexArray = new Float32Array(lineData.dottedVertices.length * 3);

    for (let o = 0; o < dottedVertices.length; o++) {
      vertexArray.set(
        [
          dottedVertices[o].x - offset.x,
          dottedVertices[o].y - offset.y,
          dottedVertices[o].z ? dottedVertices[o].z : 0,
        ],
        o * 3,
      );
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(vertexArray, 3));

    geometry.computeBoundingSphere();

    const lineCollection = new THREE.LineSegments(geometry, material);
    lineCollection.position.set(offset.x, offset.y, lineCollection.position.z);
    lineCollection.name = 'LineCollection';
    lineCollection.renderOrder = renderOrders.dxfAsset.line;
    return lineCollection;
  }

  generateDashedLines(lineSegments) {
    let vertices = lineSegments.vertices;
    let type = lineSegments.properties.lineType;
    let scale = lineSegments.properties.lineTypeScale;

    if (!type || type.length === 0) {
      return vertices;
    }

    if (type.length % 2 !== 0) {
      console.log({
        message: 'Warning, unknown linetype format, default to straight line',
        linetype: type,
      });
      return vertices;
    }

    let lineTypeScale = 0.25 * (scale ? scale : 1);

    let dashes = [];

    for (let i = 0; i < type.length; i += 2) {
      if (type[i] === 0) {
        type[i] = 0.6;
      }

      if (type[i] < 0 || type[i + 1] > 0 || !parseFloat(type[i]) || !parseFloat(type[i + 1])) {
        console.log({
          message: 'Warning, unknown linetype format, default to straight line',
          linetype: type,
        });
        return vertices;
      }

      dashes.push(Math.abs(type[i] * lineTypeScale));
      dashes.push(Math.abs(type[i + 1] * lineTypeScale));
    }

    if (vertices.length % 2 !== 0) {
      console.log({
        message: 'Warning, uneven number of vertices, default to straight line',
        vertices: vertices,
        linetype: type,
      });
      return vertices;
    }

    let newVertices = [];
    let di = 0;
    let lasti = -2;
    let connectedLine = false;

    let accumulatedDistance = 0;
    let lastSegmentLength = 0;

    for (let i = 0; i < vertices.length; i += 2) {
      let a = vertices[i];
      let b = vertices[i + 1];

      let segmentLength = getDistance3D(a, b);

      //Skip generating pattern if a segment is longer than 10 km
      if (segmentLength > 10000) {
        newVertices.push(a);
        newVertices.push(b);
        continue;
      }

      let travelledDistance = 0;
      if (accumulatedDistance === 0) {
        di = 0;
      }

      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (travelledDistance + dashes[di % dashes.length] >= segmentLength) {
          if (i > 0) {
            connectedLine = compareVertices(vertices[i - 1], vertices[i]);
            if (connectedLine) {
              accumulatedDistance += lastSegmentLength;
              if (accumulatedDistance > dashes[di]) {
                di++;
                accumulatedDistance = 0;
              }
            } else {
              accumulatedDistance = 0;
            }
          }

          lastSegmentLength = segmentLength;

          if (connectedLine && di % 2 !== 0) {
            break;
          }

          if (i - lasti === 2 && travelledDistance === 0) {
            newVertices.push(a);
          }
          if (newVertices.length % 2 !== 0) {
            newVertices.push(b);
          }
          break;
        }

        //Dash
        if (di % 2 === 0) {
          let dashStart = translatePoint3D(a, b, travelledDistance);
          let dashEnd = translatePoint3D(a, b, travelledDistance + dashes[di % dashes.length]);

          if (newVertices.length % 2 === 0) {
            newVertices.push(dashStart);
          }

          newVertices.push(dashEnd);
        } else {
          let gapStart = translatePoint3D(a, b, travelledDistance);
          let gapEnd = translatePoint3D(a, b, travelledDistance + dashes[di % dashes.length]);

          if (newVertices.length % 2 !== 0) {
            newVertices.push(gapStart);
          }

          newVertices.push(gapEnd);
        }

        travelledDistance += dashes[di % dashes.length];
        di++;
      }
      lasti = i;
    }

    return newVertices;
  }
}
